/**
 * @file Labs parent-form fields and create-flow ownership.
 */

import {
  appData as configuredAppData,
  state as configuredState,
} from "../../app/state/state.js";
import { openParentForm } from "../../shared/forms/parentFormModal.js";
import { registerParentFormAdapter } from "../../shared/forms/parentFormRegistry.js";
import { setFormItemData } from "../../shared/forms/formModalState.js";
import {
  closeForm,
  configureDefaultFormActions,
  formModalElement,
} from "../../shared/forms/modalShell.js";
import {
  FIELD_WRAPPER_CLASSES,
  FIXED_TEXTAREA_CLASSES,
  getFieldLabelHtml,
  getReadOnlyValueHtml,
} from "../../shared/forms/nativeFieldStyles.js";
import { renderDynamicFieldForm } from "../../shared/forms/dynamicFields.js";
import { createSelectControl } from "../../shared/ui/controls/selectControl.js";
import {
  destroyRegisteredControlsIn,
  getRegisteredControl,
} from "../../shared/ui/controls/controlCore.js";
import { openConfirmationModal } from "../../shared/ui/confirmationModal.js";
import { resolveItemClosedFromStatus } from "../../shared/utils/closedState.js";
import {
  saveLabItem,
  updateLabPatientLink,
} from "./labsApi.js";
import {
  attachLabsPatientLinkFieldListeners,
  getLabsPatientDisplay,
  mountLabsPatientCreateControl,
  openLabsPatientLookupModal,
  renderLabsPatientCreateField,
  renderLabsPatientLinkField,
} from "./labsPatientLookupModal.js";

const INTERNAL_STREAM = "internal";
const EXTERNAL_STREAM = "external";
const INTERNAL_LAB_ID = "internal";
const DEFAULT_INTERNAL_LAB_LABEL = "Internal lab";
const LABS_STREAMS = Object.freeze([
  {
    value: EXTERNAL_STREAM,
    label: "External",
    icon: "local_shipping",
    body: "Sent to an external lab for production.",
  },
  {
    value: INTERNAL_STREAM,
    label: "In-house",
    icon: "person_apron",
    body: "For lab work completed internally.",
  },
]);

function attrValue(value = "") {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function textValue(value = "") {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function fieldHtml(label, controlHtml, options = {}) {
  const wrapperClass = [FIELD_WRAPPER_CLASSES, options.className || ""]
    .filter(Boolean)
    .join(" ");
  return `
    <div class="${wrapperClass}">
      ${getFieldLabelHtml(label, Boolean(options.required))}
      ${controlHtml}
    </div>
  `;
}

function textareaHtml({name, value = "", placeholder = ""}) {
  const placeholderAttr = placeholder ?
    ` placeholder="${attrValue(placeholder)}"` :
    "";
  return `<textarea name="${name}" class="${FIXED_TEXTAREA_CLASSES}"${placeholderAttr}>${textValue(value)}</textarea>`;
}

function getOptionSupportsStream(option, stream) {
  const streams = Array.isArray(option?.streams) ?
    option.streams.map((entry) => String(entry)).filter(Boolean) :
    [];
  return streams.length === 0 || streams.includes(stream);
}

function getDefaultStreamOptionId(options = [], stream = "") {
  const activeOptions = (Array.isArray(options) ? options : [])
    .filter((option) => option?.isActive !== false)
    .filter((option) => getOptionSupportsStream(option, stream));
  return activeOptions.find((option) => option.default)?.id ||
    activeOptions[0]?.id ||
    "";
}

function getLabsStream(itemData = {}) {
  return itemData?.stream === INTERNAL_STREAM ? INTERNAL_STREAM : EXTERNAL_STREAM;
}

function getInternalLabLabel(appData = configuredAppData) {
  return String(appData?.config?.labs?.internalLabLabel || "").trim() ||
    DEFAULT_INTERNAL_LAB_LABEL;
}

function getCurrentUserId() {
  return configuredState.currentUser?.uid ||
    configuredState.currentUserProfile?.id ||
    configuredState.currentUserProfile?.uid ||
    "";
}

function getCurrentUserProfile() {
  const currentUserId = getCurrentUserId();
  return configuredState.currentUserProfile ||
    (configuredAppData.users || []).find((user) => user.id === currentUserId) ||
    null;
}

function isActiveUser(userId) {
  const user = (configuredAppData.users || [])
    .find((entry) => entry.id === userId);
  if (user) {return user.isActive !== false;}
  const profile = getCurrentUserProfile();
  return Boolean(userId) &&
    userId === getCurrentUserId() &&
    profile?.isActive !== false;
}

function getDefaultAssignedUserForStream(stream) {
  const configuredUserId =
    configuredAppData?.config?.labs?.defaultAssignedUserByStream?.[stream] ||
    "";
  if (isActiveUser(configuredUserId)) {return configuredUserId;}
  return getCurrentUserId();
}

function getCreatorProviderDefault() {
  const currentUserId = getCurrentUserId();
  const profile = getCurrentUserProfile();
  return currentUserId &&
    profile?.isAppProvider === true &&
    profile?.isActive !== false ?
    currentUserId :
    "";
}

function getActiveExternalLabOptions(appData, selectedLabId = "") {
  const selectedValue = String(selectedLabId || "");
  return (Array.isArray(appData?.config?.labs?.externalLabs) ?
    appData.config.labs.externalLabs :
    [])
    .filter((entry) => {
      return entry?.id &&
        (entry.isActive !== false || String(entry.id) === selectedValue);
    })
    .map((entry) => ({
      value: String(entry.id),
      label: String(entry.name || entry.id),
      order: Number.isFinite(Number(entry.order)) ? Number(entry.order) : 9999,
      archived: entry.isActive === false,
      selectedOnly: entry.isActive === false,
      unavailableLabel: entry.isActive === false ? "archived" : "",
    }))
    .sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
}

function getLabsTypeOptions({appData, selectedTypes = [], stream}) {
  return (appData?.config?.labTypes || [])
    .filter((option) => {
      return selectedTypes.includes(option?.id) ||
        option?.isActive !== false && getOptionSupportsStream(option, stream);
    });
}

function renderStreamPickerFields() {
  return `
    <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
      ${LABS_STREAMS.map((stream) => `
        <button
          type="button"
          class="labs-stream-choice group flex min-h-32 flex-col items-center justify-center rounded-lg border border-gray-200 bg-white p-4 text-center shadow-sm transition-colors hover:border-[var(--dfc-brand-primary)] hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--dfc-brand-primary-ring)] sm:min-h-36"
          data-labs-stream="${stream.value}"
        >
          <span class="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-black transition-colors group-hover:bg-[var(--dfc-brand-primary)] group-hover:text-[var(--dfc-brand-primary-contrast)]">
            <span class="material-symbols-outlined text-[25px] leading-none">${stream.icon}</span>
          </span>
          <span class="text-base font-semibold text-gray-900">${stream.label}</span>
          <span class="mt-1 max-w-44 text-xs leading-5 text-gray-600">${stream.body}</span>
        </button>
      `).join("")}
    </div>
  `;
}

function syncLabsDynamicFields(typeOptions, selectedTypeIds, itemForm) {
  const selectedSet = new Set(selectedTypeIds);
  itemForm.querySelectorAll('[id^="dynamic-fields-"]:not(#dynamic-fields-anchor)')
    .forEach((container) => {
      const typeId = container.id.replace("dynamic-fields-", "");
      if (!selectedSet.has(typeId)) {
        destroyRegisteredControlsIn(container);
        container.remove();
      }
    });

  selectedTypeIds.forEach((typeId) => {
    const typeConfig = typeOptions.find((option) => option.id === typeId);
    if (typeConfig) {
      renderDynamicFieldForm(typeConfig, itemForm);
    }
  });
}

function getKnownPatient(patientId) {
  if (!patientId) {return null;}
  return (configuredAppData?.patients || [])
    .find((patient) => patient.id === patientId) ||
    null;
}

function getPreparedLabsDefaults(defaults = {}) {
  const prepared = {...defaults};
  const linkedPatientId = prepared.linked_patient_id || prepared.patientId || "";
  const patient = getKnownPatient(linkedPatientId);

  prepared.stream = getLabsStream(prepared);
  if (linkedPatientId) {
    prepared.linked_patient_id = linkedPatientId;
    prepared.parentId = linkedPatientId;
  }
  if (patient) {
    prepared.patient_name = patient.name || prepared.patient_name || "";
    prepared.case_file_number = patient.case_file_number || "";
    prepared.card_id = patient.card_id || "";
    prepared.linked_patient_types = Array.isArray(patient.types) ?
      patient.types :
      [];
    if (!Array.isArray(prepared.provider) || prepared.provider.length === 0) {
      prepared.provider = Array.isArray(patient.provider) ? patient.provider : [];
    }
  } else if (prepared.patientName && !prepared.patient_name) {
    prepared.patient_name = prepared.patientName;
  }
  if (!Array.isArray(prepared.provider) || prepared.provider.length === 0) {
    const creatorProviderId = getCreatorProviderDefault();
    if (creatorProviderId) {
      prepared.provider = [creatorProviderId];
    }
  }
  if (
    !prepared.id &&
    (!Array.isArray(prepared.assigned_users) ||
      prepared.assigned_users.length === 0)
  ) {
    const assignedUserId = getDefaultAssignedUserForStream(prepared.stream);
    if (assignedUserId) {
      prepared.assigned_users = [assignedUserId];
    }
  }

  return prepared;
}

function renderLabsLabField({appData, stream}) {
  if (stream === INTERNAL_STREAM) {
    return `
      <input type="hidden" name="lab" value="${INTERNAL_LAB_ID}">
      ${fieldHtml("Lab", getReadOnlyValueHtml(textValue(getInternalLabLabel(appData))))}
    `;
  }

  return fieldHtml("Lab", '<div data-labs-scalar-control="lab"></div>', {
    required: true,
  });
}

function setSelectedPatient({formFields, formItemData, patient}) {
  formItemData.linked_patient_id = patient?.id || "";
  formItemData.patient_name = patient?.name || "";
  formItemData.case_file_number = patient?.case_file_number || "";
  formItemData.card_id = patient?.card_id || "";
  formItemData.linked_patient_types = Array.isArray(patient?.types) ?
    patient.types :
    [];
  if (Array.isArray(patient?.provider) && patient.provider.length > 0) {
    formItemData.provider = patient.provider;
    syncProviderControlFromFormData(formFields, formItemData);
  }
  renderPatientLinkFieldInForm({formFields, formItemData});
}

function syncProviderControlFromFormData(formFields, formItemData) {
  const providerRoot = formFields.querySelector(
    '[data-labs-user-control="provider"]',
  );
  const control = providerRoot?.dataset?.controlId ?
    getRegisteredControl(providerRoot.dataset.controlId) :
    null;
  control?.setValue?.(
    Array.isArray(formItemData.provider) ? formItemData.provider : [],
  );
}

function clearSelectedPatient({formFields, formItemData}) {
  const nameInput = formFields.querySelector('input[name="patient_name"]');
  formItemData.patient_name = nameInput?.value || formItemData.patient_name || "";
  formItemData.linked_patient_id = "";
  formItemData.case_file_number = "";
  formItemData.card_id = "";
  formItemData.linked_patient_types = [];
  renderPatientLinkFieldInForm({formFields, formItemData});
}

function renderPatientLinkFieldInForm({formFields, formItemData, isEditing = false}) {
  const field = formFields.querySelector(
    '[data-linked-parent-field="labs-linked-patient"]',
  );
  if (!field) {return;}

  field.outerHTML = renderLabsPatientLinkField({
    source: formItemData,
    canLink: true,
    canUnlink: true,
  });
  attachLabsCreatePatientLinkListeners({formFields, formItemData, isEditing});
}

function getLabLinkFailureMessage(result) {
  const blockerMessage = (result?.blockers || [])
    .map((blocker) => blocker?.message)
    .filter(Boolean)
    .join(" ");
  return blockerMessage || "The Lab Item patient link could not be updated.";
}

function applyLinkResultToFormData({formItemData, result, fallbackPatient}) {
  const current = result?.current || {};
  formItemData.linked_patient_id = current.patientId || fallbackPatient?.id || "";
  formItemData.patient_name = current.patientName || fallbackPatient?.name || "";
  formItemData.case_file_number =
    current.case_file_number || fallbackPatient?.case_file_number || "";
  formItemData.card_id = current.card_id || fallbackPatient?.card_id || "";
  formItemData.updatedAt = current.updatedAt || formItemData.updatedAt || null;
  formItemData.linked_patient_types = Array.isArray(current.types) ?
    current.types :
    (Array.isArray(fallbackPatient?.types) ? fallbackPatient.types : []);
}

function mergeLabPatientLinkIntoCache({
  fallbackPatient = null,
  formItemData = {},
  labId,
  result = {},
}) {
  if (!labId) {return;}
  const current = result?.current || {};
  const item = {
    id: labId,
    linked_patient_id: current.patientId || fallbackPatient?.id || null,
    patient_name:
      current.patientName ||
      fallbackPatient?.name ||
      formItemData.patient_name ||
      "",
    case_file_number:
      current.case_file_number || fallbackPatient?.case_file_number || "",
    card_id: current.card_id || fallbackPatient?.card_id || "",
    linked_patient_types: Array.isArray(current.types) ?
      current.types :
      (Array.isArray(fallbackPatient?.types) ? fallbackPatient.types : []),
    updatedAt:
      current.updatedAt || formItemData.updatedAt || new Date().toISOString(),
  };
  upsertLabsCacheItem(item);
}

function attachLabsCreatePatientLinkListeners({
  formFields,
  formItemData,
  isEditing = false,
}) {
  attachLabsPatientLinkFieldListeners(formFields, {
    onLink: () => {
      openLabsPatientLookupModal({
        selectedPatientId: formItemData.linked_patient_id || "",
        selectedPatient: getLabsPatientDisplay(formItemData),
        onSelect: async (patient) => {
          if (isEditing && formItemData.id) {
            const result = await updateLabPatientLink({
              labId: formItemData.id,
              patientId: patient?.id || null,
            });
            if (result?.success === false) {
              throw new Error(getLabLinkFailureMessage(result));
            }
            applyLinkResultToFormData({
              formItemData,
              result,
              fallbackPatient: patient,
            });
            mergeLabPatientLinkIntoCache({
              fallbackPatient: patient,
              formItemData,
              labId: formItemData.id,
              result,
            });
          } else {
            setSelectedPatient({formFields, formItemData, patient});
          }
          renderPatientLinkFieldInForm({formFields, formItemData, isEditing});
          return true;
        },
      });
    },
    onUnlink: () => {
      void (async () => {
        if (isEditing && formItemData.id) {
          const result = await updateLabPatientLink({
            labId: formItemData.id,
            patientId: null,
          });
          if (result?.success === false) {
            throw new Error(getLabLinkFailureMessage(result));
          }
          mergeLabPatientLinkIntoCache({
            formItemData,
            labId: formItemData.id,
            result,
          });
        }
        clearSelectedPatient({formFields, formItemData});
      })().catch((error) => {
        openConfirmationModal(
          "Patient Link Not Updated",
          error?.message || "The Lab Item patient link could not be cleared.",
          async () => {},
          false,
          "OK",
        );
      });
    },
  });
}

function mountLabsScalarControls(context) {
  const {
    appData,
    createDateControl,
    createPrioritySelectPreset,
    createProviderSelectPreset,
    createStreamScopedStatusSelectPreset,
    createStreamScopedTypeSelectPreset,
    createUserSelectPreset,
    formFields,
    formItemData,
    itemForm,
    state,
  } = context;
  const stream = getLabsStream(formItemData);
  const controls = {};

  const providerRoot = formFields.querySelector(
    '[data-labs-user-control="provider"]',
  );
  if (providerRoot) {
    controls.provider = createProviderSelectPreset({
      root: providerRoot,
      appData,
      value: Array.isArray(formItemData.provider) ? formItemData.provider : [],
      required: true,
      searchable: true,
      onChange: (value) => {
        formItemData.provider = Array.isArray(value) ? value : [];
      },
    });
  }

  if (formItemData.id) {
    attachLabsCreatePatientLinkListeners({
      formFields,
      formItemData,
      isEditing: true,
    });
  } else {
    mountLabsPatientCreateControl({
      root: formFields.querySelector("[data-labs-patient-create-control]"),
      formItemData,
      onPatientSelected: () => {
        syncProviderControlFromFormData(formFields, formItemData);
      },
    });
  }

  const assignedRoot = formFields.querySelector(
    '[data-labs-user-control="assigned_users"]',
  );
  if (assignedRoot) {
    controls.assigned_users = createUserSelectPreset({
      root: assignedRoot,
      appData,
      name: "assigned_users",
      collectionName: "labs",
      value: Array.isArray(formItemData.assigned_users) ?
        formItemData.assigned_users :
        [],
      multiple: true,
      required: true,
      searchable: true,
      onChange: (value) => {
        formItemData.assigned_users = Array.isArray(value) ? value : [];
      },
    });
  }

  const priorityRoot = formFields.querySelector(
    '[data-labs-scalar-control="priority"]',
  );
  if (priorityRoot) {
    controls.priority = createPrioritySelectPreset({
      root: priorityRoot,
      appData,
      value: formItemData.priority ||
        getDefaultStreamOptionId(appData?.config?.priorities || [], stream),
      clearable: false,
    });
  }

  const statusRoot = formFields.querySelector(
    '[data-labs-scalar-control="status"]',
  );
  if (statusRoot) {
    controls.status = createStreamScopedStatusSelectPreset({
      root: statusRoot,
      appData,
      itemType: "labs",
      stream,
      value: formItemData.status ||
        getDefaultStreamOptionId(appData?.config?.labStatus || [], stream),
      clearable: false,
    });
    formItemData.status = controls.status.value || formItemData.status || "";
  }

  const labRoot = formFields.querySelector('[data-labs-scalar-control="lab"]');
  if (labRoot) {
    controls.lab = createSelectControl({
      root: labRoot,
      name: "lab",
      value: formItemData.lab || "",
      options: getActiveExternalLabOptions(appData, formItemData.lab),
      placeholder: "+ Add external lab",
      clearable: false,
      required: true,
      requiredMessage: "Choose an external lab.",
    });
  }

  const dueDateRoot = formFields.querySelector(
    '[data-labs-date-control="due_date"]',
  );
  if (dueDateRoot) {
    controls.due_date = createDateControl({
      root: dueDateRoot,
      name: "due_date",
      type: "date",
      value: formItemData.due_date || "",
    });
  }

  controls.types = mountLabsTypeControl({
    appData,
    createStreamScopedTypeSelectPreset,
    formFields,
    formItemData,
    itemForm,
    state,
    stream,
  });

  return controls;
}

function mountLabsTypeControl({
  appData,
  createStreamScopedTypeSelectPreset,
  formFields,
  formItemData,
  itemForm,
  state,
  stream,
}) {
  const root = formFields.querySelector("[data-labs-type-control]");
  if (!root) {return null;}

  const selectedTypes = Array.isArray(formItemData.types) ?
    formItemData.types :
    [];
  const typeOptions = getLabsTypeOptions({
    appData,
    selectedTypes,
    stream,
  });
  formItemData.types = selectedTypes;

  const control = createStreamScopedTypeSelectPreset({
    root,
    appData,
    itemType: "labs",
    stream,
    options: typeOptions,
    value: selectedTypes,
    required: true,
    scopeUser: state.currentUser,
    searchable: true,
    onChange: (value) => {
      const nextTypes = Array.isArray(value) ? value : [];
      formItemData.types = nextTypes;
      syncLabsDynamicFields(typeOptions, nextTypes, itemForm);
    },
  });
  syncLabsDynamicFields(typeOptions, selectedTypes, itemForm);
  return control;
}

function prepareLabsSubmit({data, form, formItemData}) {
  const stream = getLabsStream(formItemData);
  const selectedTypes = Array.isArray(data.types) ? data.types : [];
  const selectedProviders = Array.isArray(data.provider) ?
    data.provider :
    [];
  const selectedAssignedUsers = Array.isArray(data.assigned_users) ?
    data.assigned_users :
    [];
  if (selectedTypes.length === 0) {
    openConfirmationModal(
      "Lab Type Required",
      "Choose a stream-valid Lab type before saving.",
      async () => {
        form.querySelector("[data-labs-type-control] button")?.focus();
      },
      false,
      "OK",
    );
    return false;
  }

  if (selectedProviders.length === 0) {
    openConfirmationModal(
      "Provider Required",
      "Choose at least one Provider before saving.",
      async () => {
        form.querySelector('[data-labs-user-control="provider"] button')
          ?.focus();
      },
      false,
      "OK",
    );
    return false;
  }

  if (selectedAssignedUsers.length === 0) {
    openConfirmationModal(
      "Assigned User Required",
      "Choose at least one Assigned User before saving.",
      async () => {
        form.querySelector('[data-labs-user-control="assigned_users"] button')
          ?.focus();
      },
      false,
      "OK",
    );
    return false;
  }

  if (stream === EXTERNAL_STREAM && !data.lab) {
    openConfirmationModal(
      "External Lab Required",
      "Choose an active configured External lab before saving.",
      async () => {
        form.querySelector("[data-labs-scalar-control=\"lab\"] button")
          ?.focus();
      },
      false,
      "OK",
    );
    return false;
  }

  return true;
}

function applyLabsSubmitData({data, formItemData}) {
  const stream = getLabsStream(formItemData);
  data.stream = stream;
  data.patient_name = String(data.patient_name || formItemData.patient_name || "")
    .trim();
  data.linked_patient_id = data.linked_patient_id || null;
  data.provider = Array.isArray(data.provider) ?
    data.provider :
    formItemData.provider || [];
  data.assigned_users = Array.isArray(data.assigned_users) ?
    data.assigned_users :
    formItemData.assigned_users || [];
  data.types = Array.isArray(data.types) ? data.types : formItemData.types || [];
  data.due_date = data.due_date || null;
  data.lab = stream === INTERNAL_STREAM ? INTERNAL_LAB_ID : data.lab;
  data.cost = stream === EXTERNAL_STREAM ?
    (Object.prototype.hasOwnProperty.call(data, "cost") ?
      data.cost || null :
      formItemData.cost || null) :
    null;
}

async function saveLabsFormItem({data, id}) {
  const result = await saveLabItem({
    itemId: id || null,
    data,
  });

  if (result?.success === false) {
    throw new Error(result?.message || "Lab Item could not be saved.");
  }

  return result;
}

function upsertLabsCacheItem(item) {
  if (!item?.id) {return;}
  const items = Array.isArray(configuredAppData.labs) ?
    configuredAppData.labs :
    [];
  const existingIndex = items.findIndex((entry) => entry?.id === item.id);
  if (existingIndex >= 0) {
    configuredAppData.labs = items.map((entry, index) =>
      index === existingIndex ? {...entry, ...item} : entry,
    );
    return;
  }
  configuredAppData.labs = [item, ...items];
}

function mergeSavedLabIntoCache({
  data = {},
  formItemData = {},
  id,
  saveResult = {},
}) {
  const itemId = saveResult?.itemId || id;
  if (!itemId) {return;}
  const linkCurrent = saveResult?.initialLinkResult?.current || {};
  const existing = (Array.isArray(configuredAppData.labs) ?
    configuredAppData.labs :
    [])
    .find((entry) => entry?.id === itemId) || {};
  const hasLinkedPatientValue = Object.prototype.hasOwnProperty.call(
    data,
    "linked_patient_id",
  );
  const linkedPatientId = hasLinkedPatientValue ?
    data.linked_patient_id :
    (linkCurrent.patientId ||
      formItemData.linked_patient_id ||
      existing.linked_patient_id ||
      null);
  const status = data.status || formItemData.status || existing.status || "";
  upsertLabsCacheItem({
    ...existing,
    ...formItemData,
    ...data,
    id: itemId,
    linked_patient_id: linkedPatientId || null,
    patient_name:
      data.patient_name ||
      linkCurrent.patientName ||
      formItemData.patient_name ||
      existing.patient_name ||
      "",
    case_file_number:
      data.case_file_number ||
      linkCurrent.case_file_number ||
      formItemData.case_file_number ||
      existing.case_file_number ||
      "",
    card_id:
      data.card_id ||
      linkCurrent.card_id ||
      formItemData.card_id ||
      existing.card_id ||
      "",
    linked_patient_types: Array.isArray(data.linked_patient_types) ?
      data.linked_patient_types :
      Array.isArray(linkCurrent.types) ?
        linkCurrent.types :
        Array.isArray(formItemData.linked_patient_types) ?
          formItemData.linked_patient_types :
          (Array.isArray(existing.linked_patient_types) ?
            existing.linked_patient_types :
            []),
    provider: Array.isArray(data.provider) ?
      data.provider :
      Array.isArray(linkCurrent.provider) ?
        linkCurrent.provider :
        Array.isArray(formItemData.provider) ?
          formItemData.provider :
          (Array.isArray(existing.provider) ? existing.provider : []),
    isClosedResolved: resolveItemClosedFromStatus("lab", status),
    updatedAt:
      saveResult?.updatedAt || data.updatedAt || new Date().toISOString(),
  });
}

/**
 * Returns whether Labs create/edit forms are currently exposed.
 * @returns {boolean} True when Labs form behavior is implemented.
 */
export function isLabsFormRuntimeEnabled() {
  return true;
}

/**
 * Opens the Labs stream picker before the standard create form.
 * @param {object} [defaults={}] Optional create defaults.
 * @returns {void}
 */
export function openLabsCreateFlow(defaults = {}) {
  if (defaults?.stream) {
    openParentForm("lab", getPreparedLabsDefaults(defaults));
    return;
  }

  if (!formModalElement) {return;}

  const formTitle = formModalElement.querySelector("#form-title");
  const formHeaderActions = formModalElement.querySelector("#form-header-actions");
  const formFields = formModalElement.querySelector("#form-fields");
  const itemForm = formModalElement.querySelector("#item-form");
  const cancelBtn = document.getElementById("cancel-form-btn");
  const submitBtn = formModalElement.querySelector('button[type="submit"]');
  const footer = cancelBtn?.parentElement || submitBtn?.parentElement || null;

  if (!formTitle || !formHeaderActions || !formFields || !itemForm) {return;}

  configureDefaultFormActions();
  setFormItemData({});
  formTitle.textContent = "New Lab Item";
  formHeaderActions.innerHTML = `
    <button
      type="button"
      class="standard-action-icon-btn standard-action-icon-btn--danger shrink-0"
      aria-label="Close Lab Item stream picker"
      data-labs-stream-picker-close
    >
      <span class="material-symbols-outlined text-[20px] leading-none">close</span>
    </button>
  `;
  formFields.innerHTML = renderStreamPickerFields();
  itemForm.dataset.type = "lab-stream";
  itemForm.dataset.id = "";
  itemForm.dataset.parentId = "";
  itemForm.onsubmit = (event) => event.preventDefault();
  if (footer) {footer.style.display = "none";}
  if (cancelBtn) {
    cancelBtn.style.display = "";
    cancelBtn.onclick = closeForm;
  }
  formHeaderActions
    .querySelector("[data-labs-stream-picker-close]")
    ?.addEventListener("click", closeForm);

  formFields.querySelectorAll("[data-labs-stream]").forEach((button) => {
    button.addEventListener("click", () => {
      const stream = button.dataset.labsStream || EXTERNAL_STREAM;
      closeForm();
      openParentForm("lab", getPreparedLabsDefaults({
        ...defaults,
        stream,
      }));
    });
  });

  formModalElement.classList.add("flex");
}

/**
 * Parent-form adapter for Labs create/edit fields.
 */
export const labsFormAdapter = {
  getTitle({formItemData, isEditing}) {
    void formItemData;
    return `${isEditing ? "Edit" : "Add"} Lab Item`;
  },

  getHeaderActionsHtml({formItemData}) {
    void formItemData;
    return "";
  },

  renderFields({appData, formItemData, isEditing}) {
    const stream = getLabsStream(formItemData);
    const val = (field, fallback = "") => formItemData?.[field] ?? fallback;
    const patientField = isEditing ?
      renderLabsPatientLinkField({
        source: formItemData,
        canLink: true,
        canUnlink: true,
      }) :
      renderLabsPatientCreateField({
        className: "",
        source: formItemData,
      });

    return `
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <input type="hidden" name="stream" value="${attrValue(stream)}">
        ${patientField}
        ${fieldHtml("Provider", '<div data-labs-user-control="provider"></div>', {
          required: true,
        })}
        ${fieldHtml("Type(s)", '<div data-labs-type-control></div>', {
          required: true,
        })}
        ${renderLabsLabField({appData, stream})}
        <div id="dynamic-fields-anchor" class="contents"></div>
        ${fieldHtml("Urgency", '<div data-labs-scalar-control="priority"></div>')}
        ${fieldHtml("Status", '<div data-labs-scalar-control="status"></div>')}
        ${fieldHtml("Due Date", '<div data-labs-date-control="due_date"></div>')}
        ${fieldHtml("Assigned Users", '<div data-labs-user-control="assigned_users"></div>', {
          required: true,
        })}
        ${fieldHtml("Details", textareaHtml({
          name: "details",
          value: val("details"),
        }), {className: "md:col-span-2"})}
      </div>
    `;
  },

  mountScalarControls: mountLabsScalarControls,

  async prepareSubmit(context) {
    return prepareLabsSubmit(context);
  },

  applySubmitData: applyLabsSubmitData,

  saveItem: saveLabsFormItem,

  afterSave: mergeSavedLabIntoCache,
};

registerParentFormAdapter("lab", labsFormAdapter);
